/* Wrist-X buy box gallery. Scoped to the <wristx-gallery> custom element so it
   cannot affect other sections; supports multiple instances per page. */
if (!customElements.get('wristx-gallery')) {
  customElements.define(
    'wristx-gallery',
    class WristxGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.wristx-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.wristx-gallery__slide'));
        this.prevButton = this.querySelector('.wristx-gallery__arrow--prev');
        this.nextButton = this.querySelector('.wristx-gallery__arrow--next');
        this.thumbsWrapper = this.querySelector('.wristx-gallery__thumbs');
        this.activeIndex = 0;

        if (!this.track || this.slides.length === 0) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (this.slides.length > 1) {
          this.buildThumbs();

          if (this.prevButton && this.nextButton) {
            this.prevButton.hidden = false;
            this.nextButton.hidden = false;
            this.prevButton.addEventListener('click', () => this.scrollToIndex(this.activeIndex - 1));
            this.nextButton.addEventListener('click', () => this.scrollToIndex(this.activeIndex + 1));
          }

          this.track.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.scrollToIndex(this.activeIndex + 1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.scrollToIndex(this.activeIndex - 1);
            }
          });

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.setActive(this.slides.indexOf(entry.target));
                }
              });
            },
            { root: this.track, threshold: 0.6 }
          );
          this.slides.forEach((slide) => this.observer.observe(slide));
          this.setActive(0);

          const initial = this.indexOfMedia(this.dataset.initialMediaId);
          if (initial > 0) this.scrollToIndex(initial, 'auto');
        }
      }

      /* Index of the slide showing a given product media, or -1. Slides only
         carry a media id when the gallery falls through to product.media. */
      indexOfMedia(mediaId) {
        if (!mediaId) return -1;
        return this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      }

      showMedia(mediaId) {
        const index = this.indexOfMedia(mediaId);
        if (index < 0) return false;
        this.scrollToIndex(index);
        return true;
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      buildThumbs() {
        if (!this.thumbsWrapper) return;
        this.thumbs = this.slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'wristx-gallery__thumb';
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-label', 'Go to image ' + (index + 1));
          button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          const img = slide.querySelector('img');
          if (img) {
            const thumbImg = img.cloneNode();
            thumbImg.removeAttribute('sizes');
            thumbImg.removeAttribute('srcset');
            thumbImg.loading = 'lazy';
            thumbImg.alt = '';
            button.appendChild(thumbImg);
          }
          button.addEventListener('click', () => this.scrollToIndex(index));
          this.thumbsWrapper.appendChild(button);
          return button;
        });
      }

      scrollToIndex(index, forcedBehavior) {
        const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
        const behavior = forcedBehavior || (this.reducedMotion.matches ? 'auto' : 'smooth');
        this.track.scrollTo({ left: this.slides[clamped].offsetLeft - this.track.offsetLeft, behavior });
      }

      setActive(index) {
        if (index < 0) return;
        this.activeIndex = index;
        if (this.thumbs) {
          this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-selected', i === index ? 'true' : 'false'));
          const activeThumb = this.thumbs[index];
          if (activeThumb && this.thumbsWrapper) {
            const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
            this.thumbsWrapper.scrollTo({
              left: activeThumb.offsetLeft - this.thumbsWrapper.offsetLeft - this.thumbsWrapper.clientWidth / 2 + activeThumb.clientWidth / 2,
              behavior
            });
          }
        }
        if (this.prevButton) this.prevButton.toggleAttribute('disabled', index === 0);
        if (this.nextButton) this.nextButton.toggleAttribute('disabled', index === this.slides.length - 1);
      }
    }
  );
}

/* Quantity offers: the checked offer radio drives the hidden quantity input
   in the section's add-to-cart form. */
if (!customElements.get('wristx-offers')) {
  customElements.define(
    'wristx-offers',
    class WristxOffers extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', (event) => {
          if (!event.target.matches('.wristx-buybox__offer-input')) return;
          const form = document.getElementById(this.dataset.formId);
          if (!form) return;

          const quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput) quantityInput.value = event.target.value;

          /* Offers that name a variant swap the variant instead of the
             quantity, so the price, sale badge and button have to follow. */
          const variantId = event.target.dataset.variantId;
          if (!variantId) return;

          const variantInput = form.querySelector('.product-variant-id');
          if (variantInput) variantInput.value = variantId;

          const section = this.closest('.wristx-buybox') || document;
          const onSale = event.target.dataset.onSale === 'true';
          const price = event.target.dataset.price || '';
          const compareAtPrice = event.target.dataset.compareAtPrice || '';

          const current = section.querySelector('[data-wristx-price-current]');
          if (current) current.textContent = price;

          const compare = section.querySelector('[data-wristx-price-compare]');
          if (compare) {
            compare.textContent = compareAtPrice;
            compare.hidden = !onSale;
          }

          const badge = section.querySelector('[data-wristx-sale-badge]');
          if (badge) badge.hidden = !onSale;

          const available = event.target.dataset.available !== 'false';
          const button = form.querySelector('.wristx-buybox__atc');
          if (button) button.disabled = !available;

          const gallery = section.querySelector('wristx-gallery');
          if (gallery && typeof gallery.showMedia === 'function') {
            gallery.showMedia(event.target.dataset.featuredMediaId);
          }

          const label = form.querySelector('[data-wristx-atc-label]');
          if (label) {
            if (!available) {
              label.textContent = label.dataset.soldOutText;
            } else if (label.dataset.showPrice === 'true') {
              label.textContent = `${label.dataset.atcText} | ${price}`;
            } else {
              label.textContent = label.dataset.atcText;
            }
          }
        });
      }
    }
  );
}

/* App-only promo lines. The buy box renders the PRO notice and the upgrade
   link hidden (see section-wristx-buybox.css); they are revealed only when the
   URL marks the visit as coming from the WhyGolf app — wgpro for the PRO
   upsell, wgfree for the free-plan upgrade link. */
(function () {
  let targetSelectors = [];

  if (location.href.includes('wgpro')) {
    targetSelectors = ['[data-text="mobile-text"]', '[data-texty="mobile-texty"]'];
  } else if (location.href.includes('wgfree')) {
    targetSelectors = ['[data-free="free-text"]'];
  }

  if (!targetSelectors.length) return; // keep everything hidden on all other URLs

  function revealMobileDiscount() {
    let found = false;
    targetSelectors.forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) {
        el.classList.add('show-mobile-discount');
        found = true;
      }
    });
    return found;
  }

  if (revealMobileDiscount()) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealMobileDiscount, { once: true });
  } else {
    const observer = new MutationObserver(() => {
      if (revealMobileDiscount()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  }
})();

/* Detected buyer country, fetched once and shared. Shopify's own
   browsing-context endpoint answers this on the storefront origin — no
   third-party lookup, no rate limit. Resolves to an ISO code, or null when the
   shop can't tell us; every consumer must treat null as "don't know". */
const wristxDetectedCountry = (() => {
  let pending = null;
  return () => {
    if (pending) return pending;
    /* assets/wg-region.js (layout/theme.liquid) already did this lookup. */
    if (window.WGRegion && typeof window.WGRegion.ready === 'function') {
      pending = window.WGRegion.ready().catch(() => null);
      return pending;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    pending = fetch(root + 'browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const handle =
          data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
        return typeof handle === 'string' && handle ? handle.trim().toUpperCase() : null;
      })
      .catch(() => null);
    return pending;
  };
})();

/* Out-of-region notice. Each notice ships hidden and carries the country that
   is allowed to buy; we reveal it only when the shop positively detects a
   different one.

   This fails CLOSED on purpose: a failed, blocked, or unrecognised response
   leaves the notice hidden, so a lookup problem can never tell a buyer in a
   supported country that the product is unavailable. */
/* 'CA' -> the two regional-indicator code points that render as a flag. */
const wristxFlagFor = (iso) =>
  /^[A-Z]{2}$/.test(iso)
    ? String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : null;

(function () {
  const notices = document.querySelectorAll('.wristx-buybox__geo[data-country]');
  if (!notices.length) return;

  wristxDetectedCountry().then((detected) => {
    if (!detected) return; // unknown — stay hidden

    notices.forEach((notice) => {
      if (detected === notice.dataset.country.trim().toUpperCase()) return;

      const flag = wristxFlagFor(detected);
      const flagEl = notice.querySelector('[data-wristx-geo-flag]');
      if (flag && flagEl) {
        flagEl.textContent = flag;
        flagEl.hidden = false;
      }
      notice.hidden = false;

      // One shipping statement at a time. A ship-by bar promising delivery
      // sits directly under the button this notice is refusing, so drop it
      // rather than let the two disagree on screen.
      const buybox = notice.closest('.wristx-buybox');
      const bar = buybox && buybox.querySelector('[data-wristx-shipbar]');
      if (bar) bar.hidden = true;
    });
  });
})();

/* Ship-by bar country swap. The server paints the home-country wording, which
   is right for the large majority and survives full-page caching. Only a
   positively detected overseas visitor gets swapped, and they get a flag plus
   an ISO code rather than a delivery-speed claim we can't stand behind. */
(() => {
  const bars = document.querySelectorAll('[data-wristx-shipbar][data-localize]');
  if (!bars.length) return;

  wristxDetectedCountry().then((detected) => {
    if (!detected) return; // unknown — leave the served markup alone

    bars.forEach((bar) => {
      const home = (bar.dataset.homeCountry || 'US').trim().toUpperCase();
      if (detected === home) return;

      // Only claim to ship somewhere we actually ship. A visitor outside the
      // shippable set keeps the served markup, which promises them nothing.
      const shippable = new Set(
        (bar.dataset.shipCountries || '')
          .split(',')
          .map((v) => v.trim().toUpperCase())
          .filter(Boolean)
      );
      if (!shippable.has(detected)) return;

      const flag = wristxFlagFor(detected);
      if (!flag) return; // unrecognised code — don't render a tofu box

      const flagEl = bar.querySelector('[data-wristx-shipbar-flag]');
      const noteEl = bar.querySelector('[data-wristx-shipbar-note]');
      if (!flagEl || !noteEl) return;

      flagEl.textContent = flag;
      noteEl.textContent = `${(bar.dataset.intlPrefix || 'SHIPS TO').trim()} ${detected}`;
    });
  });
})();

/* Ship-by bar. Liquid already rendered the right day, but a full-page-cached
   response can outlive the cutoff, so re-derive it in the shop's timezone on
   load and correct the label if it drifted. Weekends and listed holidays roll
   forward to the next open business day. */
(() => {
  const bars = document.querySelectorAll('[data-wristx-shipbar]');
  if (!bars.length) return;

  const SHOP_TZ = 'America/Los_Angeles';
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Today's calendar date and wall-clock hour in the shop's timezone, DST included.
  const shopToday = (date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: SHOP_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      hour12: false,
    }).formatToParts(date);
    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24 };
  };

  const iso = (d) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

  const shipLabel = (bar, now) => {
    const cutoff = parseInt(bar.dataset.cutoffHour, 10);
    if (Number.isNaN(cutoff)) return null;

    const closed = new Set(
      (bar.dataset.holidays || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    );

    // Anchor to a synthetic UTC date so day arithmetic can't be bent by DST.
    const { year, month, day, hour } = shopToday(now);
    const base = Date.UTC(year, month - 1, day);

    let shift = hour >= cutoff ? 1 : 0;
    for (let guard = 0; guard < 21; guard += 1) {
      const probe = new Date(base + shift * 86400000);
      const wday = probe.getUTCDay();
      if (wday === 0 || wday === 6 || closed.has(iso(probe))) shift += 1;
      else break;
    }

    if (shift === 0) return bar.dataset.todayLabel;
    if (shift === 1) return bar.dataset.tomorrowLabel;

    const target = new Date(base + shift * 86400000);
    if (shift < 7) return DAYS[target.getUTCDay()].toUpperCase();
    return `${MONTHS[target.getUTCMonth()]} ${target.getUTCDate()}`.toUpperCase();
  };

  const now = new Date();
  bars.forEach((bar) => {
    const day = bar.querySelector('[data-wristx-shipbar-day]');
    if (!day) return;
    const label = shipLabel(bar, now);
    if (label && label.toUpperCase() !== day.textContent.trim().toUpperCase()) {
      day.textContent = label;
    }
  });
})();
